import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";
import { COMPANY, NAV } from "@/data/content";
import Logo from "@/components/site/Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (lenis) lenis.scrollTo(href, { offset: -20 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`nav-slide-in fixed top-0 left-0 z-50 w-full border-b transition-colors duration-500 ${
        scrolled
          ? "border-white/10 bg-[#050505]/80 backdrop-blur-xl"
          : "border-transparent bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10">
        <button
          onClick={() => (lenis ? lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }))}
          className="flex items-center gap-3 text-left"
          data-testid="nav-logo"
        >
          <Logo size={28} className="text-white shrink-0" />
          <span className="font-display text-xs font-extrabold uppercase leading-none tracking-tight md:text-sm">
            Shree Yamunesh Software Solutions
          </span>
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => go(n.href)}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className="group relative font-mono text-xs uppercase tracking-[0.15em] text-[#a1a1aa] transition-colors hover:text-white"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#0033ff] transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            onClick={() => go("#contact")}
            data-testid="nav-cta"
            className="group flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:border-[#0033ff] hover:bg-[#0033ff]"
          >
            Start a project
          </button>
        </nav>

        <button
          onClick={() => setOpen((o) => !o)}
          className="text-white md:hidden"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#050505] md:hidden"
          >
            <div className="flex flex-col px-5 py-4">
              {NAV.map((n) => (
                <button
                  key={n.href}
                  onClick={() => go(n.href)}
                  className="border-b border-white/5 py-4 text-left font-display text-lg font-extrabold uppercase tracking-tight text-white"
                  data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
